/* eslint-disable */
import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import { ChevronLeft, ChevronRight, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';

function useIsMobile(bp=768){const[m,setM]=useState(()=>window.innerWidth<bp);useEffect(()=>{const h=()=>setM(window.innerWidth<bp);window.addEventListener('resize',h);return()=>window.removeEventListener('resize',h);},[bp]);return m;}

const C = {
  cream:'#F7F6EF', charcoal:'#2F3A42', navy:'#445366', mid:'#7A8A96', border:'#D8DEE3',
  skyBlue:'#BCD9E5', skyBlueLight:'#EAF4F8', skyText:'#3A8FA3',
  green:'#7EA594', greenLight:'#EAF2EF',
  amber:'#E07B00', amberLight:'#FFF3E0',
  red:'#DC3545', redLight:'#FFF0F0',
  white:'#FFFFFF',
};

const VENUES  = ['Box Jordaan','Box Plantage','Sassys','Prep Kitchen'];
const MONTHS  = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DOW     = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
const BASE_URL = 'https://open-api.eitje.app/open_api';

const EITJE_ENV_MAP = { 3080:'Box Jordaan', 3081:'Box Plantage', 5537:'Sassys', 3660:'Prep Kitchen' };

// Normalise Eitje team names to FOH / Kitchen / Management
function normaliseDept(teamName) {
  if (!teamName) return null;
  const t = teamName.toLowerCase();
  if (t === 'foh' || t === 'service') return 'FOH';
  if (t === 'kitchen' || t === 'kp' || t === 'prep kitchen' || t === 'keuken') return 'Kitchen';
  if (t === 'management' || t === 'algemeen') return 'Management';
  return teamName;
}

const STATUS_STYLE = {
  accepted:{ bg:C.greenLight, border:C.green, text:C.green, label:'Accepted' },
  pending: { bg:C.amberLight, border:C.amber, text:C.amber, label:'Pending'  },
  denied:  { bg:C.redLight,   border:C.red,   text:C.red,   label:'Denied'   },
};

function pill(active) {
  return { padding:'5px 14px', borderRadius:'20px', border:'none', background:active?C.navy:C.cream, color:active?C.white:C.mid, fontSize:'13px', fontWeight:600, cursor:'pointer', transition:'all 0.15s' };
}
function smPill(active) {
  return { padding:'4px 10px', borderRadius:'20px', border:'none', background:active?C.navy:C.cream, color:active?C.white:C.mid, fontSize:'12px', fontWeight:600, cursor:'pointer', transition:'all 0.15s' };
}

function fmt(iso)     { if(!iso)return'—'; return new Date(iso).toLocaleDateString('en-GB',{day:'numeric',month:'short'}); }
function fmtFull(iso) { if(!iso)return'—'; return new Date(iso).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}); }
function isoDate(d)   { return d.toISOString().slice(0,10); }
function addDays(date,n){ const d=new Date(date); d.setDate(d.getDate()+n); return d; }
function daysSince(iso){ if(!iso)return 0; return Math.floor((Date.now()-new Date(iso))/86400000); }

function AgeBadge({createdAt}) {
  const days = daysSince(createdAt);
  if (days < 7) return null;
  return (
    <span style={{padding:'2px 7px',borderRadius:'20px',fontSize:'10px',fontWeight:700,background:C.amberLight,color:C.amber,border:`1px solid ${C.amber}44`,whiteSpace:'nowrap'}}>
      {days>=14 ? <><span style={{color:C.red}}>⚠</span> {days}d overdue</> : `${days}d pending`}
    </span>
  );
}

// ── Coverage context — same dept ─────────────────────────────────────────────
function coverageForRequest(req, allReqs) {
  const dept = normaliseDept(req.team?.name || req.dept);
  const start = new Date(req.start_datetime);
  const end   = new Date(req.end_datetime);
  // Week window
  const weekStart = new Date(start);
  weekStart.setDate(weekStart.getDate() - ((weekStart.getDay()+6)%7));
  const weekEnd = addDays(weekStart, 6);

  return allReqs.filter(r => {
    if (r.id === req.id) return false;
    if (!['accepted','pending'].includes(r.status)) return false;
    const rd = normaliseDept(r.team?.name || r.dept);
    if (dept && rd && dept !== rd) return false; // same dept only
    const rs = new Date(r.start_datetime);
    const re = new Date(r.end_datetime);
    return rs <= weekEnd && re >= weekStart;
  });
}

// ── Detail modal ─────────────────────────────────────────────────────────────
function LeaveDetail({req, allReqs, onClose}) {
  if (!req) return null;
  const s    = STATUS_STYLE[req.status] || STATUS_STYLE.pending;
  const start = new Date(req.start_datetime);
  const end   = new Date(req.end_datetime);
  const days  = Math.max(1, Math.ceil((end-start)/86400000));
  const age   = daysSince(req.created_at);
  const dept  = normaliseDept(req.team?.name);
  const others = coverageForRequest(req, allReqs);

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.35)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:'20px'}} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{background:C.white,borderRadius:'14px',padding:'24px',maxWidth:'420px',width:'100%',boxShadow:'0 8px 40px rgba(0,0,0,0.18)',border:`2px solid ${s.border}44`}}>
        <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:'16px'}}>
          <div>
            <div style={{fontSize:'16px',fontWeight:800,color:C.charcoal}}>{req.user?.name}</div>
            <div style={{fontSize:'12px',color:C.mid,marginTop:'2px'}}>
              {EITJE_ENV_MAP[req.environment?.id]||req.environment?.name}
              {dept && <span style={{marginLeft:'6px',padding:'1px 7px',borderRadius:'20px',background:C.skyBlueLight,color:C.skyText,fontSize:'10px',fontWeight:700}}>{dept}</span>}
            </div>
          </div>
          <span style={{padding:'4px 12px',borderRadius:'20px',background:s.bg,color:s.text,fontSize:'12px',fontWeight:700,border:`1px solid ${s.border}44`}}>{s.label}</span>
        </div>

        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px',marginBottom:'14px'}}>
          {[['From',fmtFull(req.start_datetime)],['To',fmtFull(req.end_datetime)],['Duration',`${days} day${days!==1?'s':''}`],['Reason',req.reason||'—']].map(([k,v])=>(
            <div key={k} style={{background:C.cream,borderRadius:'8px',padding:'10px 12px'}}>
              <div style={{fontSize:'10px',fontWeight:700,color:C.mid,textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:'3px'}}>{k}</div>
              <div style={{fontSize:'13px',color:C.charcoal,fontWeight:500}}>{v}</div>
            </div>
          ))}
        </div>

        {req.status==='pending' && (
          <div style={{marginBottom:'14px',padding:'10px 14px',borderRadius:'8px',background:age>=14?C.amberLight:age>=10?C.amberLight:C.cream,border:`1px solid ${age>=14?C.red:age>=10?C.amber:C.border}44`}}>
            <div style={{fontSize:'12px',fontWeight:700,color:age>=14?C.red:age>=10?C.amber:C.mid}}>
              {age>=14?<><span style={{color:C.red}}>⚠</span> {age} days pending — legally deemed approved under Dutch law</>
               :age>=10?`${age} days pending — response due within ${14-age} days`
               :`${age} days since submitted`}
            </div>
          </div>
        )}

        {/* Same-dept coverage */}
        <div style={{background:others.length>0?C.amberLight:C.greenLight,borderRadius:'10px',padding:'12px 14px',border:`1px solid ${others.length>0?C.amber:C.green}44`,marginBottom:'14px'}}>
          <div style={{fontSize:'11px',fontWeight:700,color:others.length>0?C.amber:C.green,textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:others.length>0?'8px':'0'}}>
            Overlapping Leave Requests{dept?` for ${dept}`:''}
          </div>
          {others.length===0
            ? <div style={{fontSize:'13px',color:C.green,fontWeight:600}}>✓ No one else from this dept off this week</div>
            : <div style={{display:'flex',flexDirection:'column',gap:'4px'}}>
                {others.map(r=>(
                  <div key={r.id} style={{display:'flex',alignItems:'center',gap:'8px'}}>
                    <span style={{fontSize:'12px',color:C.charcoal,fontWeight:600}}>{r.user?.name?.split(' ')[0]}</span>
                    <span style={{fontSize:'10px',padding:'1px 7px',borderRadius:'20px',background:STATUS_STYLE[r.status]?.bg,color:STATUS_STYLE[r.status]?.text,fontWeight:700}}>{STATUS_STYLE[r.status]?.label}</span>
                    <span style={{fontSize:'11px',color:C.mid}}>{fmt(r.start_datetime)}–{fmt(r.end_datetime)}</span>
                  </div>
                ))}
              </div>
          }
        </div>

        <button onClick={onClose} style={{width:'100%',padding:'10px',background:C.navy,color:C.white,border:'none',borderRadius:'8px',fontWeight:700,fontSize:'13px',cursor:'pointer'}}>Close</button>
      </div>
    </div>
  );
}

// ── Desktop Calendar View ─────────────────────────────────────────────────────
function CalendarView({requests, year, month}) {
  const [detail, setDetail] = useState(null);
  const firstDay    = new Date(year, month, 1);
  const daysInMonth = new Date(year, month+1, 0).getDate();
  const startDow    = (firstDay.getDay()+6)%7;
  const days = [];
  for(let i=0;i<startDow;i++) days.push(null);
  for(let i=1;i<=daysInMonth;i++) days.push(i);
  const today = new Date();

  function reqsForDay(day) {
    if (!day) return [];
    const ds = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
    return requests.filter(r => ds>=r.start_datetime.slice(0,10) && ds<=r.end_datetime.slice(0,10));
  }

  // Build spanning blocks — group consecutive days of same request into rows
  // We render spans as absolute positioned elements per week row
  const weeks = [];
  for (let i=0; i<days.length; i+=7) weeks.push(days.slice(i,i+7));

  function spansForWeek(weekDays, weekIdx) {
    const spans = [];
    const seen  = new Set();
    weekDays.forEach((day, colIdx) => {
      if (!day) return;
      const ds = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
      reqsForDay(day).forEach(r => {
        if (seen.has(r.id)) return;
        seen.add(r.id);
        // Find start col and end col in this week
        let startCol = colIdx;
        let endCol   = colIdx;
        for (let c=colIdx+1; c<weekDays.length; c++) {
          if (!weekDays[c]) break;
          const nextDs = `${year}-${String(month+1).padStart(2,'0')}-${String(weekDays[c]).padStart(2,'0')}`;
          if (nextDs >= r.start_datetime.slice(0,10) && nextDs <= r.end_datetime.slice(0,10)) endCol = c;
          else break;
        }
        spans.push({ r, startCol, endCol });
      });
    });
    return spans;
  }

  const COL_W = 100/7;

  return (
    <>
      {detail && <LeaveDetail req={detail} allReqs={requests} onClose={()=>setDetail(null)}/>}
      <div style={{borderRadius:'12px',border:`1px solid ${C.border}`,overflow:'hidden'}}>
        <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',background:C.cream,borderBottom:`1px solid ${C.border}`}}>
          {DOW.map(d=><div key={d} style={{padding:'8px',textAlign:'center',fontSize:'11px',fontWeight:700,color:C.mid}}>{d}</div>)}
        </div>
        {weeks.map((weekDays, wi) => {
          const spans = spansForWeek(weekDays, wi);
          // Count spans per col for vertical offset
          const colCount = {};
          const spanRows = spans.map(sp => {
            let row = 0;
            for (let c=sp.startCol; c<=sp.endCol; c++) { row = Math.max(row, colCount[c]||0); }
            for (let c=sp.startCol; c<=sp.endCol; c++) { colCount[c] = row+1; }
            return { ...sp, row };
          });
          const maxRows = Math.max(0, ...Object.values(colCount));
          const cellH   = Math.max(60, 28 + maxRows*22);

          return (
            <div key={wi} style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',borderBottom:`1px solid ${C.border}`,position:'relative',minHeight:`${cellH}px`}}>
              {weekDays.map((day,ci) => {
                const isToday = day && new Date(year,month,day).toDateString()===today.toDateString();
                return (
                  <div key={ci} style={{borderRight:ci<6?`1px solid ${C.border}`:'none',padding:'5px 6px',minHeight:`${cellH}px`,background:day?C.white:C.cream}}>
                    {day && (
                      <div style={{fontSize:'12px',fontWeight:isToday?800:500,color:isToday?C.white:C.mid,background:isToday?C.navy:'transparent',width:22,height:22,borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center'}}>{day}</div>
                    )}
                  </div>
                );
              })}
              {/* Spanning leave blocks */}
              {spanRows.map(({r, startCol, endCol, row}, si) => {
                const st = STATUS_STYLE[r.status]||STATUS_STYLE.pending;
                const overdue = r.status==='pending' && daysSince(r.created_at)>=14;
                const left  = `calc(${startCol*COL_W}% + 2px)`;
                const width = `calc(${(endCol-startCol+1)*COL_W}% - 4px)`;
                const top   = 28 + row*22;
                return (
                  <div key={si} onClick={()=>setDetail(r)} style={{
                    position:'absolute', left, width, top, height:20,
                    background:st.bg, border:`1.5px solid ${st.border}`,
                    borderRadius:'4px', cursor:'pointer', display:'flex', alignItems:'center',
                    paddingLeft:'6px', overflow:'hidden', zIndex:2,
                  }}>
                    {overdue && <span style={{color:C.red,marginRight:'3px',fontSize:'9px'}}>⚠</span>}
                    <span style={{fontSize:'9px',fontWeight:700,color:st.text,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                      {r.user?.name?.split(' ')[0]}
                    </span>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </>
  );
}

// ── Mobile Calendar View ──────────────────────────────────────────────────────
function MobileCalendarView({requests, year, month, vf}) {
  const [selectedDay, setSelectedDay] = useState(new Date().getDate());
  const [detail, setDetail] = useState(null);
  const firstDay    = new Date(year, month, 1);
  const daysInMonth = new Date(year, month+1, 0).getDate();
  const startDow    = (firstDay.getDay()+6)%7;
  const today       = new Date();
  const days = [];
  for(let i=0;i<startDow;i++) days.push(null);
  for(let i=1;i<=daysInMonth;i++) days.push(i);

  function reqsForDay(day) {
    if (!day) return [];
    const ds = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
    return requests.filter(r => ds>=r.start_datetime.slice(0,10) && ds<=r.end_datetime.slice(0,10));
  }

  const selReqs = reqsForDay(selectedDay);
  const selDs   = selectedDay ? `${year}-${String(month+1).padStart(2,'0')}-${String(selectedDay).padStart(2,'0')}` : null;
  const selDate = selectedDay ? new Date(year,month,selectedDay) : null;
  const selDow  = selDate ? DOW[(selDate.getDay()+6)%7] : '';

  return (
    <>
      {detail && <LeaveDetail req={detail} allReqs={requests} onClose={()=>setDetail(null)}/>}

      {/* Compact calendar grid */}
      <div style={{borderRadius:'12px',border:`1px solid ${C.border}`,overflow:'hidden',marginBottom:'16px'}}>
        <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',background:C.cream,borderBottom:`1px solid ${C.border}`}}>
          {DOW.map(d=><div key={d} style={{padding:'6px 2px',textAlign:'center',fontSize:'10px',fontWeight:700,color:C.mid}}>{d}</div>)}
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)'}}>
          {days.map((day,i) => {
            const isToday  = day && new Date(year,month,day).toDateString()===today.toDateString();
            const isSel    = day===selectedDay;
            const dayReqs  = reqsForDay(day);
            const hasPend  = dayReqs.some(r=>r.status==='pending');
            const hasAcc   = dayReqs.some(r=>r.status==='accepted');
            const hasOverdue = dayReqs.some(r=>r.status==='pending'&&daysSince(r.created_at)>=14);
            return (
              <div key={i} onClick={()=>day&&setSelectedDay(day)} style={{
                padding:'4px 2px', minHeight:'44px', textAlign:'center',
                background: isSel ? C.navy : day ? C.white : C.cream,
                border:`1px solid ${C.border}`, borderLeft:'none', borderTop:'none',
                cursor:day?'pointer':'default',
              }}>
                {day && (
                  <>
                    <div style={{fontSize:'12px',fontWeight:isToday?800:500,color:isSel?C.white:isToday?C.navy:C.charcoal,marginBottom:'2px'}}>{day}</div>
                    <div style={{display:'flex',gap:'2px',justifyContent:'center',flexWrap:'wrap'}}>
                      {hasPend && <div style={{width:6,height:6,borderRadius:'50%',background:hasOverdue?(isSel?C.white:C.red):(isSel?C.white:C.amber)}}/>}
                      {hasAcc  && <div style={{width:6,height:6,borderRadius:'50%',background:isSel?C.white:C.green}}/>}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Selected day detail */}
      <div>
        <div style={{fontSize:'13px',fontWeight:800,color:C.charcoal,marginBottom:'10px'}}>
          {selDate ? `${selDow}, ${selectedDay} ${MONTHS[month]}` : 'Select a day'}
          {selReqs.length > 0 && <span style={{marginLeft:'8px',padding:'2px 8px',borderRadius:'20px',background:C.navy+'22',color:C.navy,fontSize:'11px',fontWeight:700}}>{selReqs.length}</span>}
        </div>
        {selReqs.length===0 ? (
          <div style={{padding:'20px',textAlign:'center',color:C.mid,fontSize:'13px',background:C.cream,borderRadius:'10px'}}>No leave requests this day</div>
        ) : (
          <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
            {selReqs.map(r => {
              const st  = STATUS_STYLE[r.status]||STATUS_STYLE.pending;
              const dur = Math.max(1,Math.ceil((new Date(r.end_datetime)-new Date(r.start_datetime))/86400000));
              const dept = normaliseDept(r.team?.name);
              return (
                <div key={r.id} onClick={()=>setDetail(r)} style={{display:'flex',alignItems:'center',gap:'10px',padding:'12px 14px',background:C.white,borderRadius:'10px',border:`1px solid ${st.border}33`,cursor:'pointer'}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:'13px',fontWeight:700,color:C.charcoal}}>{r.user?.name}</div>
                    <div style={{fontSize:'11px',color:C.mid,marginTop:'2px',display:'flex',gap:'6px',flexWrap:'wrap'}}>
                      {EITJE_ENV_MAP[r.environment?.id]||r.environment?.name}
                      {dept && <span style={{padding:'0px 5px',borderRadius:'20px',background:C.skyBlueLight,color:C.skyText,fontWeight:600}}>{dept}</span>}
                      <span>{fmt(r.start_datetime)}–{fmt(r.end_datetime)} · {dur}d</span>
                    </div>
                    {r.reason && <div style={{fontSize:'11px',color:C.mid,marginTop:'2px',fontStyle:'italic'}}>{r.reason}</div>}
                  </div>
                  <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:'4px'}}>
                    <span style={{padding:'3px 8px',borderRadius:'20px',background:st.bg,color:st.text,fontSize:'11px',fontWeight:700}}>{st.label}</span>
                    {r.status==='pending' && <AgeBadge createdAt={r.created_at}/>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

// ── List View ─────────────────────────────────────────────────────────────────
function ListView({requests, members, vf, df, mobileOnly=false}) {
  const [detail,   setDetail]   = useState(null);
  const [expanded, setExpanded] = useState({});

  const statusesToShow = mobileOnly ? ['pending'] : ['pending','accepted','denied'];

  const people = useMemo(() => {
    function findDept(req) { return normaliseDept(req.team?.name) || '—'; }
    const map = {};
    requests.forEach(r => {
      if (!statusesToShow.includes(r.status)) return;
      const key   = r.user?.name||'Unknown';
      const venue = EITJE_ENV_MAP[r.environment?.id]||r.environment?.name||'—';
      const dept  = findDept(r);
      if (vf!=='All' && venue!==vf) return;
      if (df!=='All' && dept!==df)  return;
      if (!map[key]) map[key]={ name:key, venue, dept, pending:[], accepted:[], denied:[] };
      if (r.status==='pending')  map[key].pending.push(r);
      if (r.status==='accepted') map[key].accepted.push(r);
      if (r.status==='denied')   map[key].denied.push(r);
    });
    return Object.values(map).sort((a,b)=>{
      const ap=a.pending.length,bp=b.pending.length;
      if(bp!==ap) return bp-ap;
      if(a.venue!==b.venue) return a.venue.localeCompare(b.venue);
      return a.name.localeCompare(b.name);
    });
  }, [requests, vf, df, mobileOnly]);

  function toggle(name){ setExpanded(p=>({...p,[name]:!p[name]})); }

  if (people.length===0) return (
    <div style={{padding:'40px',textAlign:'center',color:C.mid,fontSize:'13px',background:C.cream,borderRadius:'12px'}}>
      {mobileOnly ? 'No pending leave requests' : 'No leave requests match this filter'}
    </div>
  );

  const colStyle = {padding:'10px 14px',fontSize:'12px',fontWeight:600,textAlign:'center'};
  const hdrStyle = {...colStyle,fontSize:'11px',fontWeight:700,color:C.mid,textTransform:'uppercase',letterSpacing:'0.05em',background:C.cream};

  if (mobileOnly) {
    // Mobile: simple cards, pending only
    return (
      <>
        {detail && <LeaveDetail req={detail} allReqs={requests} onClose={()=>setDetail(null)}/>}
        <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
          {people.map(p => {
            const isOpen = !!expanded[p.name];
            const hasUrgent = p.pending.some(r=>daysSince(r.created_at)>=14);
            const hasWarn   = p.pending.some(r=>daysSince(r.created_at)>=10);
            return (
              <div key={p.name} style={{borderRadius:'10px',border:`1px solid ${hasUrgent?C.amber:C.border}`,overflow:'hidden',background:hasUrgent?C.amberLight:C.white}}>
                <div onClick={()=>toggle(p.name)} style={{display:'flex',alignItems:'center',gap:'10px',padding:'12px 14px',cursor:'pointer'}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:'13px',fontWeight:700,color:C.charcoal}}>{p.name}</div>
                    <div style={{fontSize:'11px',color:C.mid,marginTop:'2px'}}>{p.venue} · {p.dept}</div>
                  </div>
                  <span style={{padding:'3px 10px',borderRadius:'20px',background:hasUrgent?C.amberLight:C.amberLight,color:C.amber,fontWeight:700,fontSize:'12px',border:`1px solid ${C.amber}44`}}>
                    {hasUrgent && <span style={{color:C.red}}>⚠ </span>}{p.pending.length} pending
                  </span>
                  {isOpen ? <ChevronUp size={14} color={C.mid}/> : <ChevronDown size={14} color={C.mid}/>}
                </div>
                {isOpen && (
                  <div style={{background:C.cream,borderTop:`1px solid ${C.border}`,padding:'8px 12px',display:'flex',flexDirection:'column',gap:'6px'}}>
                    {p.pending.map(r => {
                      const dur = Math.max(1,Math.ceil((new Date(r.end_datetime)-new Date(r.start_datetime))/86400000));
                      return (
                        <div key={r.id} onClick={()=>setDetail(r)} style={{display:'flex',alignItems:'center',gap:'10px',padding:'10px 12px',background:C.white,borderRadius:'8px',border:`1px solid ${C.amber}33`,cursor:'pointer'}}>
                          <div style={{flex:1}}>
                            <div style={{fontSize:'12px',fontWeight:600,color:C.charcoal}}>{fmt(r.start_datetime)} → {fmt(r.end_datetime)} · {dur}d</div>
                            {r.reason && <div style={{fontSize:'11px',color:C.mid,marginTop:'1px'}}>{r.reason}</div>}
                          </div>
                          <AgeBadge createdAt={r.created_at}/>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </>
    );
  }

  // Desktop list
  return (
    <>
      {detail && <LeaveDetail req={detail} allReqs={requests} onClose={()=>setDetail(null)}/>}
      <div style={{borderRadius:'12px',border:`1px solid ${C.border}`,overflow:'hidden'}}>
        <div style={{display:'grid',gridTemplateColumns:'2fr 1fr 1fr 1fr 1fr 0.5fr',borderBottom:`2px solid ${C.border}`}}>
          <div style={{...hdrStyle,textAlign:'left',paddingLeft:'16px'}}>Team Member</div>
          <div style={{...hdrStyle,color:C.mid}}>Dept</div>
          <div style={{...hdrStyle,color:C.amber}}>Pending</div>
          <div style={{...hdrStyle,color:C.green}}>Approved</div>
          <div style={{...hdrStyle,color:C.red}}>Denied</div>
          <div style={{...hdrStyle}}/>
        </div>
        {people.map((p,i) => {
          const isOpen    = !!expanded[p.name];
          const hasUrgent = p.pending.some(r=>daysSince(r.created_at)>=14);
          const hasWarn   = p.pending.some(r=>daysSince(r.created_at)>=10);
          const rowBg     = (hasUrgent||hasWarn)?C.amberLight:i%2===0?C.white:'#FAFAF8';
          return (
            <div key={p.name}>
              <div onClick={()=>toggle(p.name)} style={{display:'grid',gridTemplateColumns:'2fr 1fr 1fr 1fr 1fr 0.5fr',borderBottom:`1px solid ${C.border}`,background:rowBg,cursor:'pointer'}}>
                <div style={{...colStyle,textAlign:'left',paddingLeft:'16px'}}>
                  <div style={{fontWeight:700,fontSize:'13px',color:C.charcoal}}>{p.name}</div>
                  <div style={{fontSize:'11px',color:C.mid,marginTop:'2px'}}>{p.venue}</div>
                </div>
                <div style={{...colStyle,textAlign:'left'}}>
                  <span style={{padding:'2px 8px',borderRadius:'20px',background:C.skyBlueLight,color:C.skyText,fontSize:'11px',fontWeight:600}}>{p.dept}</span>
                </div>
                <div style={{...colStyle}}>
                  {p.pending.length>0
                    ? <span style={{padding:'3px 10px',borderRadius:'20px',background:C.amberLight,color:C.amber,fontWeight:700,fontSize:'13px',border:`1px solid ${C.amber}44`}}>
                        {hasUrgent&&<span style={{color:C.red}}>⚠ </span>}{p.pending.length}
                      </span>
                    : <span style={{color:C.border}}>—</span>}
                </div>
                <div style={{...colStyle}}>
                  {p.accepted.length>0
                    ? <span style={{padding:'3px 10px',borderRadius:'20px',background:C.greenLight,color:C.green,fontWeight:700,fontSize:'13px',border:`1px solid ${C.green}44`}}>{p.accepted.length}</span>
                    : <span style={{color:C.border}}>—</span>}
                </div>
                <div style={{...colStyle}}>
                  {p.denied.length>0
                    ? <span style={{padding:'3px 10px',borderRadius:'20px',background:C.redLight,color:C.red,fontWeight:700,fontSize:'13px',border:`1px solid ${C.red}44`}}>{p.denied.length}</span>
                    : <span style={{color:C.border}}>—</span>}
                </div>
                <div style={{...colStyle,display:'flex',alignItems:'center',justifyContent:'center'}}>
                  {isOpen?<ChevronUp size={14} color={C.mid}/>:<ChevronDown size={14} color={C.mid}/>}
                </div>
              </div>
              {isOpen && (
                <div style={{background:C.cream,borderBottom:`1px solid ${C.border}`,padding:'10px 16px',display:'flex',flexDirection:'column',gap:'6px'}}>
                  {[...p.pending,...p.accepted,...p.denied].map(r=>{
                    const st  = STATUS_STYLE[r.status]||STATUS_STYLE.pending;
                    const dur = Math.max(1,Math.ceil((new Date(r.end_datetime)-new Date(r.start_datetime))/86400000));
                    return (
                      <div key={r.id} onClick={()=>setDetail(r)} style={{display:'flex',alignItems:'center',gap:'10px',padding:'10px 14px',background:C.white,borderRadius:'8px',border:`1px solid ${st.border}33`,cursor:'pointer'}}>
                        <span style={{padding:'2px 8px',borderRadius:'20px',background:st.bg,color:st.text,fontSize:'11px',fontWeight:700,flexShrink:0}}>{st.label}</span>
                        <div style={{flex:1}}>
                          <div style={{fontSize:'12px',fontWeight:600,color:C.charcoal}}>{fmt(r.start_datetime)} → {fmt(r.end_datetime)}<span style={{marginLeft:'8px',color:C.mid,fontWeight:400}}>{dur}d</span></div>
                          {r.reason && <div style={{fontSize:'11px',color:C.mid,marginTop:'1px'}}>{r.reason}</div>}
                        </div>
                        {r.status==='pending' && <AgeBadge createdAt={r.created_at}/>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════
// MAIN
// ══════════════════════════════════════════════════════════════════════
export default function LeaveCalendar({members, currentUser}) {
  const isMobile = useIsMobile();
  const [creds,    setCreds]    = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [lastSync, setLastSync] = useState(null);

  // Desktop: calendar | list. Mobile: list | calendar
  const [view,    setView]    = useState(isMobile ? 'list' : 'calendar');
  const [vf,      setVf]      = useState('All');
  const [df,      setDf]      = useState('All');
  const [statusF, setStatusF] = useState(['accepted','pending','denied']);

  const now = new Date();
  const [calYear,  setCalYear]  = useState(now.getFullYear());
  const [calMonth, setCalMonth] = useState(now.getMonth());

  const DEPTS = ['FOH','Kitchen','Management'];

  useEffect(() => {
    async function loadCreds() {
      const {data,error} = await supabase.from('app_settings').select('key,value')
        .in('key',['eitje_partner_username','eitje_partner_password','eitje_api_username','eitje_api_password']);
      if (error||!data||data.length<4) { setError('Eitje credentials not configured.'); setLoading(false); return; }
      const c={};
      data.forEach(r=>{c[r.key]=r.value;});
      setCreds(c);
    }
    loadCreds();
  }, []);

  useEffect(()=>{if(creds)fetchLeave(creds);},[creds]);

  function getHeaders(c) {
    return {'Content-Type':'application/json','Partner-Username':c.eitje_partner_username,'Partner-Password':c.eitje_partner_password,'Api-Username':c.eitje_api_username,'Api-Password':c.eitje_api_password};
  }

  async function fetchLeave(c) {
    setLoading(true); setError(null);
    try {
      const h = getHeaders(c);
      const s1=isoDate(now), e1=isoDate(addDays(now,89));
      const s2=isoDate(addDays(now,90)), e2=isoDate(addDays(now,179));
      const s3=isoDate(addDays(now,180)), e3=isoDate(addDays(now,269));
      const [r1,r2,r3] = await Promise.all([
        fetch(`${BASE_URL}/leave_requests?filters[start_date]=${s1}&filters[end_date]=${e1}`,{method:'GET',headers:h}),
        fetch(`${BASE_URL}/leave_requests?filters[start_date]=${s2}&filters[end_date]=${e2}`,{method:'GET',headers:h}),
        fetch(`${BASE_URL}/leave_requests?filters[start_date]=${s3}&filters[end_date]=${e3}`,{method:'GET',headers:h}),
      ]);
      const [t1,t2,t3] = await Promise.all([r1.text(),r2.text(),r3.text()]);
      if(!t1) throw new Error(`Empty response — ${r1.status}`);
      const j1=JSON.parse(t1);
      if(!r1.ok) throw new Error(j1?.message||`${r1.status}`);
      let items2=[], items3=[];
      if(t2){try{const j2=JSON.parse(t2);if(r2.ok)items2=j2.items||[];}catch{}}
      if(t3){try{const j3=JSON.parse(t3);if(r3.ok)items3=j3.items||[];}catch{}}
      const all=[...(j1.items||[]),...items2,...items3];
      const seen=new Set();
      setRequests(all.filter(r=>{if(seen.has(r.id))return false;seen.add(r.id);return true;}));
      setLastSync(new Date());
    } catch(e){ setError(e.message); }
    finally{ setLoading(false); }
  }

  function refresh(){ if(creds)fetchLeave(creds); }
  function toggleStatus(s){ setStatusF(p=>p.includes(s)?p.filter(x=>x!==s):[...p,s]); }

  const filtered = useMemo(()=>requests.filter(r=>{
    if(!isMobile && !statusF.includes(r.status)) return false;
    const venue=EITJE_ENV_MAP[r.environment?.id]||r.environment?.name;
    if(vf!=='All'&&venue!==vf) return false;
    return true;
  }),[requests,statusF,vf,isMobile]);

  const counts = useMemo(()=>({
    pending:  requests.filter(r=>r.status==='pending').length,
    accepted: requests.filter(r=>r.status==='accepted').length,
    denied:   requests.filter(r=>r.status==='denied').length,
    urgent:   requests.filter(r=>r.status==='pending'&&daysSince(r.created_at)>=14).length,
    warning:  requests.filter(r=>r.status==='pending'&&daysSince(r.created_at)>=10&&daysSince(r.created_at)<14).length,
  }),[requests]);

  if (loading) return <div style={{padding:'60px',textAlign:'center',color:C.mid,fontSize:'14px'}}>Loading leave requests from Eitje…</div>;
  if (error)   return <div style={{padding:'24px',background:C.redLight,borderRadius:'12px',border:`1px solid ${C.red}44`,color:C.red,fontSize:'13px'}}><strong>Could not load Eitje data:</strong> {error}</div>;

  return (
    <div>
      {/* Header */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'16px',flexWrap:'wrap',gap:'10px'}}>
        <div>
          <h3 style={{margin:'0 0 2px',fontSize:'16px',fontWeight:800,color:C.charcoal}}>Leave Requests</h3>
          <div style={{fontSize:'12px',color:C.mid}}>
            Live · {requests.length} requests · ~6 months
            {lastSync&&` · ${lastSync.toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'})}`}
          </div>
        </div>
        <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
          <button onClick={refresh} style={{display:'flex',alignItems:'center',gap:'5px',padding:'6px 12px',border:`1px solid ${C.border}`,borderRadius:'8px',background:C.white,color:C.mid,fontSize:'12px',cursor:'pointer',fontWeight:600}}>
            <RefreshCw size={12}/> Refresh
          </button>
          <div style={{display:'flex',gap:'2px',background:C.cream,borderRadius:'8px',padding:'3px'}}>
            {(isMobile?['list','calendar']:['calendar','list']).map(v=>(
              <button key={v} onClick={()=>setView(v)} style={{padding:'5px 14px',borderRadius:'6px',border:'none',background:view===v?C.white:'transparent',color:view===v?C.navy:C.mid,fontSize:'12px',fontWeight:view===v?700:500,cursor:'pointer',boxShadow:view===v?'0 1px 3px rgba(0,0,0,0.1)':'none',transition:'all 0.15s',textTransform:'capitalize'}}>{v}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Urgent banner */}
      {(counts.urgent>0||counts.warning>0) && (
        <div style={{marginBottom:'16px',padding:'12px 16px',borderRadius:'10px',background:counts.urgent>0?C.amberLight:C.amberLight,border:`1px solid ${C.amber}44`,display:'flex',alignItems:'flex-start',gap:'10px'}}>
          <span style={{fontSize:'16px',marginTop:'1px'}}>{counts.urgent>0?<span style={{color:C.red}}>⚠</span>:'⚑'}</span>
          <div>
            {counts.urgent>0&&<div style={{fontSize:'13px',fontWeight:700,color:C.red}}>{counts.urgent} request{counts.urgent!==1?'s':''} legally overdue — 14+ days pending. Under Dutch law these are deemed approved.</div>}
            {counts.warning>0&&<div style={{fontSize:'13px',fontWeight:600,color:C.amber,marginTop:counts.urgent>0?'4px':0}}>{counts.warning} request{counts.warning!==1?'s':''} approaching the 14-day deadline.</div>}
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{display:'flex',gap:'8px',marginBottom:'16px',flexWrap:'wrap',alignItems:'center'}}>
        {/* Status pills — desktop only */}
        {!isMobile && ['pending','accepted','denied'].map(s=>{
          const st=STATUS_STYLE[s]; const active=statusF.includes(s);
          return <button key={s} onClick={()=>toggleStatus(s)} style={{padding:'4px 12px',borderRadius:'20px',cursor:'pointer',fontSize:'12px',fontWeight:600,border:`1px solid ${active?st.border:C.border}`,background:active?st.bg:C.white,color:active?st.text:C.mid,transition:'all 0.15s'}}>
            {st.label} {counts[s]>0&&<strong>{counts[s]}</strong>}
          </button>;
        })}
        {/* Venue + dept pills */}
        <div style={{display:'flex',gap:'6px',marginLeft:isMobile?0:'auto',flexWrap:'wrap',alignItems:'center'}}>
          {['All',...VENUES].map(v=>(
            <button key={v} onClick={()=>setVf(v)} style={isMobile?smPill(vf===v):pill(vf===v)}>{v==='All'?'All':v.replace('Box ','')}</button>
          ))}
          {!isMobile && <>
            <div style={{width:1,height:20,background:C.border,margin:'0 4px'}}/>
            {['All',...DEPTS].map(d=>(
              <button key={d} onClick={()=>setDf(d)} style={pill(df===d)}>{d==='All'?'All Depts':d}</button>
            ))}
          </>}
        </div>
      </div>

      {/* Calendar nav */}
      {view==='calendar' && (
        <div style={{display:'flex',alignItems:'center',gap:'12px',marginBottom:'16px'}}>
          <button onClick={()=>{if(calMonth===0){setCalMonth(11);setCalYear(y=>y-1);}else setCalMonth(m=>m-1);}} style={{padding:'6px',border:`1px solid ${C.border}`,borderRadius:'8px',background:C.white,cursor:'pointer',display:'flex',alignItems:'center'}}><ChevronLeft size={16} color={C.mid}/></button>
          <span style={{fontWeight:700,fontSize:'15px',color:C.charcoal,minWidth:'160px',textAlign:'center'}}>{MONTHS[calMonth]} {calYear}</span>
          <button onClick={()=>{if(calMonth===11){setCalMonth(0);setCalYear(y=>y+1);}else setCalMonth(m=>m+1);}} style={{padding:'6px',border:`1px solid ${C.border}`,borderRadius:'8px',background:C.white,cursor:'pointer',display:'flex',alignItems:'center'}}><ChevronRight size={16} color={C.mid}/></button>
          <button onClick={()=>{setCalYear(now.getFullYear());setCalMonth(now.getMonth());}} style={{padding:'5px 12px',border:`1px solid ${C.border}`,borderRadius:'8px',background:C.white,color:C.mid,fontSize:'12px',cursor:'pointer',fontWeight:600}}>Today</button>
        </div>
      )}

      {/* Legend — desktop only */}
      {!isMobile && (
        <div style={{display:'flex',gap:'12px',marginBottom:'12px',flexWrap:'wrap',alignItems:'center'}}>
          {Object.entries(STATUS_STYLE).map(([key,s])=>(
            <div key={key} style={{display:'flex',alignItems:'center',gap:'5px'}}>
              <div style={{width:10,height:10,borderRadius:'2px',background:s.bg,border:`1px solid ${s.border}`}}/>
              <span style={{fontSize:'11px',color:C.mid,fontWeight:600}}>{s.label}</span>
            </div>
          ))}
          <span style={{fontSize:'11px',color:C.mid,fontWeight:600}}><span style={{color:C.red}}>⚠</span> = 14+ days pending (legally overdue)</span>
        </div>
      )}

      {/* Views */}
      {view==='calendar' && !isMobile && <CalendarView requests={filtered} year={calYear} month={calMonth}/>}
      {view==='calendar' &&  isMobile && <MobileCalendarView requests={filtered} year={calYear} month={calMonth} vf={vf}/>}
      {view==='list'     && !isMobile && <ListView requests={filtered} members={members} vf={vf} df={df}/>}
      {view==='list'     &&  isMobile && <ListView requests={requests} members={members} vf={vf} df={df} mobileOnly={true}/>}
    </div>
  );
}